/**
 * Set Target Modal
 *
 * Compact modal for setting emissions reduction targets.
 * Based on Loveable design pattern.
 */

import { useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';
import { QuickSetupTab } from './QuickSetupTab';
import { AdvancedTab } from './AdvancedTab';
import { TargetProjection } from './TargetProjection';
import type { CreateTargetRequest } from '../../types/emissions';
import { TargetFramework } from '../../types/emissions';
import { useCreateTargetMutation } from '../../api/emissions';

type TabType = 'quick' | 'advanced';

const SET_TARGET_TABS: TabItem<TabType>[] = [
  { key: 'quick', label: 'Quick Setup' },
  { key: 'advanced', label: 'Advanced' },
];

interface SetTargetModalProps {
  isOpen: boolean;
  toggle: () => void;
  initiativeId: string;
  currentEmissions?: number;
  onSuccess?: () => void;
}

const currentYear = new Date().getFullYear();

const getDefaultFormData = (): CreateTargetRequest => ({
  framework: TargetFramework.SBTi,
  reductionPercentage: 30,
  baselineYear: currentYear,
  targetYear: currentYear + 6,
});

export const SetTargetModal = ({
  isOpen,
  toggle,
  initiativeId,
  currentEmissions,
  onSuccess,
}: SetTargetModalProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('quick');
  const [formData, setFormData] = useState<CreateTargetRequest>(getDefaultFormData);
  const [error, setError] = useState<string | null>(null);

  const [createTarget, { isLoading }] = useCreateTargetMutation();

  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
    setError(null);
  }, []);

  const handleFormChange = useCallback((data: Partial<CreateTargetRequest>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setError(null);
  }, []);

  const validateForm = (): string | null => {
    if (!formData.framework) {
      return 'Please select a target framework.';
    }
    if (formData.reductionPercentage <= 0 || formData.reductionPercentage > 100) {
      return 'Reduction percentage must be between 1 and 100.';
    }
    if (!formData.baselineYear || !formData.targetYear) {
      return 'Please select both baseline and target years.';
    }
    if (formData.targetYear <= formData.baselineYear) {
      return 'Target year must be after baseline year.';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await createTarget({ initiativeId, data: formData }).unwrap();

      // Reset form state
      setFormData(getDefaultFormData());
      setActiveTab('quick');
      setError(null);

      onSuccess?.();
      toggle();
    } catch (err: unknown) {
      let errorMessage = 'Failed to save target. Please try again.';
      if (err && typeof err === 'object') {
        const errorObj = err as Record<string, unknown>;
        if (errorObj.data && typeof errorObj.data === 'object') {
          const data = errorObj.data as Record<string, unknown>;
          if (typeof data.userMessage === 'string') {
            errorMessage = data.userMessage;
          } else if (typeof data.message === 'string') {
            errorMessage = data.message;
          }
        } else if (typeof errorObj.message === 'string') {
          errorMessage = errorObj.message;
        }
      }
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    setFormData(getDefaultFormData());
    setActiveTab('quick');
    setError(null);
    toggle();
  };

  const targetEmissions = currentEmissions
    ? currentEmissions * (1 - formData.reductionPercentage / 100)
    : undefined;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} backdrop='static' centered>
      <ModalHeader toggle={handleClose} >
        <i className='fa fa-bullseye text-success mr-2' />
        <span className='fw-semibold'>Set Emissions Reduction Target</span>
        <div className='tw:text-muted-foreground tw:mt-1 tw:text-xs tw:font-normal'>
          Define your organization&apos;s carbon reduction goals aligned with climate science
        </div>
      </ModalHeader>

      <ModalBody className='pt-3 pb-2'>
        <TabNav<TabType>
          variant='links'
          className='tw:mb-3 tw:w-full [&_[role=tab]]:tw:flex-1'
          tabs={SET_TARGET_TABS}
          activeTab={activeTab}
          onChange={handleTabChange}
        />

        {error && (
          <div className='alert alert-danger py-2 mb-3'>
            <i className='fa fa-exclamation-circle me-2' />
            {error}
          </div>
        )}

        {activeTab === 'quick' ? (
          <QuickSetupTab formData={formData} onFormChange={handleFormChange} />
        ) : (
          <AdvancedTab formData={formData} onFormChange={handleFormChange} />
        )}

        {/* Target Projection */}
        <TargetProjection
          currentEmissions={currentEmissions}
          targetEmissions={targetEmissions}
          reductionPercentage={formData.reductionPercentage}
          baselineYear={formData.baselineYear}
          targetYear={formData.targetYear}
        />

        {/* SBTi Info Banner */}
        {formData.framework === TargetFramework.SBTi && (
          <div
            className='d-flex gap-2 p-3 rounded mt-3'
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.08)' }}
          >
            <i className='fa fa-info-circle text-primary mt-1' />
            <small className='text-primary'>
              Targets aligned with Science Based Targets initiative (SBTi) require at least 4.2% annual
              reduction to limit warming to 1.5°C.
            </small>
          </div>
        )}
      </ModalBody>

      <ModalFooter className='border-0 pt-0'>
        <Button variant='secondary' onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            'Save Target'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
